import { CheckCircle2, ArrowRight, Phone, Mail, FileText, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/Button.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import { useTenant } from '../../context/TenantContext.jsx';

export function InquirySuccessView({ inquiryId, onNavigate }) {
  const { tenant } = useTenant();

  const partnerName = tenant?.name || 'ASL Partner';
  const displayId = inquiryId || 'Ihre Anfrage';

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-16 sm:px-6 lg:px-8 space-y-12">
      
      {/* Success Hero */}
      <div className="text-center space-y-4">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mx-auto shadow-sm ring-8 ring-emerald-50/50">
          <CheckCircle2 className="h-10 w-10" />
        </div>

        <span className="inline-block rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-bold uppercase tracking-wider">
          Anfrage erfolgreich übermittelt
        </span>

        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--foreground)] tracking-tight">
          Vielen Dank für Ihre Anfrage!
        </h1>

        <p className="text-base text-[var(--secondary)] max-w-xl mx-auto leading-relaxed">
          Ihre Anfrage wurde erfolgreich an das Fachteam von{' '}
          <strong className="text-[var(--foreground)]">{partnerName}</strong> übermittelt.
        </p>

        {inquiryId && (
          <div className="inline-flex items-center gap-2 rounded-xl border border-[var(--border)] bg-[var(--surface-container-low)] px-4 py-2 text-sm">
            <span className="text-[var(--secondary)]">Anfragenummer:</span>
            <span className="font-mono font-bold text-[var(--foreground)]">{displayId}</span>
          </div>
        )}
      </div>

      {/* 3-Step Process Card */}
      <Card className="border-[var(--border)] bg-white shadow-sm overflow-hidden">
        <div className="bg-[var(--surface-container-low)] px-6 py-4 border-b border-[var(--border)]">
          <h3 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
            Wie geht es jetzt weiter?
          </h3>
        </div>
        <CardContent className="p-6 space-y-6">
          <div className="grid gap-6 sm:grid-cols-3">
            
            <div className="space-y-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--primary)] text-[var(--on-primary)] font-bold text-sm">
                1
              </div>
              <h4 className="font-bold text-sm text-[var(--foreground)]">Eingang bestätigt</h4>
              <p className="text-xs text-[var(--secondary)] leading-relaxed">
                Ihre Anfrage ist eingegangen und wird direkt durch einen Fachberater geprüft.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--primary)] text-[var(--on-primary)] font-bold text-sm">
                2
              </div>
              <h4 className="font-bold text-sm text-[var(--foreground)]">Konditionsprüfung</h4>
              <p className="text-xs text-[var(--secondary)] leading-relaxed">
                Verfügbarkeit und Preise für Ihre Anfrage werden für Sie kalkuliert.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--primary)] text-[var(--on-primary)] font-bold text-sm">
                3
              </div>
              <h4 className="font-bold text-sm text-[var(--foreground)]">Verbindliches Angebot</h4>
              <p className="text-xs text-[var(--secondary)] leading-relaxed">
                Sie erhalten Ihr detailliertes Angebot per E-Mail zur finalen Freigabe.
              </p>
            </div>

          </div>
        </CardContent>
      </Card>

      {/* Direct Contact Box */}
      <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface-container-low)] p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="space-y-1 text-center sm:text-left">
          <h4 className="font-bold text-sm text-[var(--foreground)]">Haben Sie dringende Fragen zu Ihrer Anfrage?</h4>
          <p className="text-xs text-[var(--secondary)]">
            Unser Kundenservice steht Ihnen gerne telefonisch oder per E-Mail zur Verfügung.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {tenant?.phone && (
            <a
              href={`tel:${tenant.phone.replace(/[^0-9+]/g, '')}`}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius)] border border-[var(--border)] bg-white px-3 py-2 text-xs font-bold text-[var(--foreground)] hover:bg-[var(--surface-container-low)]"
            >
              <Phone className="h-3.5 w-3.5 text-[var(--primary)]" />
              <span>{tenant.phone}</span>
            </a>
          )}

          {tenant?.contactEmail && (
            <a
              href={`mailto:${tenant.contactEmail}`}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius)] border border-[var(--border)] bg-white px-3 py-2 text-xs font-bold text-[var(--foreground)] hover:bg-[var(--surface-container-low)]"
            >
              <Mail className="h-3.5 w-3.5 text-[var(--primary)]" />
              <span>E-Mail senden</span>
            </a>
          )}
        </div>
      </div>

      {/* Back to Catalog CTA */}
      <div className="text-center pt-4">
        <Button
          size="lg"
          onClick={() => onNavigate('/catalog')}
          className="gap-2 font-bold cursor-pointer"
        >
          <span>Weitere Artikel im Katalog ansehen</span>
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

    </div>
  );
}

export { InquirySuccessView as SuccessView };
